import { Transition } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import { IUserSubscriptionList } from '@type/v1-api-types';
import { StepShowBenefits, StepAskReason } from './close-flow-steps';

// Common transition for all steps
// *****************************************
const commonTransition = {
  enter: 'transition-all duration-300 delay-75',
  enterFrom: 'opacity-0 translate-y-2 min-h-96',
  enterTo: 'opacity-100 translate-y-0 min-h-max',
  leave: 'transition-all duration-300 absolute inset-0',
  leaveFrom: 'opacity-100 translate-y-0 min-h-max',
  leaveTo: 'opacity-0 translate-y-2 min-h-96 hidden',
};


/**
 * @description Renders the upgrade subscription hub view, with payment options, pricing and if
 * it's the case the 'not enough credits' and payment status possible steps.
 *
 * * **closeModal** - The function to close the modal.
 *
 * @example
 * // usage in a modal
 * import { Button, Modal } from '@components/common';
 * import { useBjModal } from 'src/hooks';
 *
 * <Button onClick={openModal}>Open upgrade packages modal</Button>
 * <Modal
 *   isOpen={isOpen}
 *   onClose={closeModal}
 *   size="lg"
 *   header={t('subscription.limit.reached.title')}
 *   body={<UpgradeSubscriptionHub closeModal={closeModal} />}
 * />
 */
export const CloseTalentSubscriptionHubModal = ({
  setReason, setOtherReason, step, userSubscriptionInfo,
}: { setReason: (reasonId: number) => void, setOtherReason: (reasonText: string) => void, step: number, userSubscriptionInfo?: IUserSubscriptionList }) => {
  const { t } = useTranslation('common');

  return (
    <div className="relative min-h-96 bg-surface-100">
      <div className="p-6">
        <div className="text-4xl font-bold">{t('premium.talent.closeRequest.modal.title')}</div>
        <div className="font-normal">{t('premium.talent.closeRequest.modal.subtitle', { endDate: userSubscriptionInfo?.nextChargeAt })}</div>

        <hr className="my-6 border-gray-400" />

        {/* STEP 1: Show benefits
        ******************************************************** */}
        <Transition
          appear
          show={step === 1}
          as="div"
          {...commonTransition}
        >
          <StepShowBenefits />
        </Transition>

        {/* STEP 2: Ask for reason
        ******************************************************** */}
        <Transition
          appear
          show={step === 2}
          as="div"
          {...commonTransition}
        >
          <StepAskReason setReason={setReason} setOtherReason={setOtherReason} />
        </Transition>
      </div>
    </div>
  );
};
